import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { getServices } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

export default function ServicesIndex() {
  const [items, setItems] = useState([]);
  const reduced = useReducedMotion();

  useEffect(() => {
    getServices().then(setItems).catch(() => {});
  }, []);

  useSEO({
    title: "Services — Cyber Security Capabilities | RockLadder Technologies",
    description:
      "Ten deeply-integrated cybersecurity capabilities — MSSP, MDR/XDR, VAPT, cloud security, IAM, SOC, EDR, threat intel, SASE, and consulting.",
    canonical: "https://rockladder.com/services",
  });

  return (
    <>
      <PageHero
        eyebrow="OUR CAPABILITIES"
        title="A unified defense"
        gradient="stack."
        subtitle="Ten operational layers. One accountable partner. Engineered for enterprises that need outcomes — not another dashboard."
        breadcrumb={[{ label: "Services" }]}
      />
      <section className="relative py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {items.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={reduced ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: EASE, delay: (i % 6) * 0.05 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-index-card-${s.slug}`}
                  className="group relative flex flex-col justify-between h-full rounded-2xl glass-panel p-7 md:p-8 overflow-hidden transition-[border-color,box-shadow] duration-500 hover:border-gold/70 hover:shadow-[0_20px_60px_-20px_rgba(139,92,246,0.55)]"
                >
                  <span className="pointer-events-none absolute top-0 left-0 h-[2px] w-10 bg-gradient-to-r from-gold to-transparent opacity-70 group-hover:w-20 transition-[width] duration-500" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">
                      {String(s.order).padStart(2, "0")}
                    </div>
                    <h3 className="font-display text-xl font-semibold text-inkText-primary leading-tight">
                      {s.name}
                    </h3>
                    <p className="mt-3 text-sm md:text-[15px] leading-[1.65] text-inkText-muted">
                      {s.short_desc}
                    </p>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-gold-body font-mono text-[11px] tracking-[0.24em] group-hover:text-gold">
                    LEARN MORE
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
